import { Component, effect, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { UIStore } from './core/services/ui.store';
import { ToastComponent } from './core/components/toast/toast.component';
import { AuthStore } from './core/auth/state/auth.store';
import { VoiceAssistantComponent } from './core/components/voice-assistant/voice-assistant.component';
import { QuotaService } from './core/auth/services/quota.service';
import { QuotaUpgradeModalComponent } from './shared/components/quota-upgrade-modal/quota-upgrade-modal.component';
import { PipelineLoaderPopupComponent } from './shared/components/pipeline-loader/pipeline-loader-popup.component';
import { JivuCommandHandoffDialogComponent } from './features/JivuCommand/components/jivu-command-handoff-dialog.component';
import { RenderLoaderComponent } from './core/render-runtime/render-loader.component';
import { JivuCommandPollerService } from './features/JivuCommand/services/jivu-command-poller.service';
import { BackgroundRenderClientService } from './core/render-runtime/worker/background-render-client.service';
import { WebavCanvasService } from './features/super-editor/media/webav-canvas.service';
import { environment } from '../environments/environment';
import { DirtyChangesDialogComponent } from './shared/components/dirty-changes-dialog/dirty-changes-dialog.component';
import { AppUpdateService } from './core/services/app-update.service';
import { LocalAiDownloadOverlayComponent } from './core/components/local-ai-download-overlay/local-ai-download-overlay.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastComponent, VoiceAssistantComponent, QuotaUpgradeModalComponent, PipelineLoaderPopupComponent, JivuCommandHandoffDialogComponent, RenderLoaderComponent, DirtyChangesDialogComponent, LocalAiDownloadOverlayComponent, CommonModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  public readonly uiStore = inject(UIStore);
  private readonly router = inject(Router);
  public readonly authStore = inject(AuthStore);
  public readonly quotaService = inject(QuotaService);
  public readonly appUpdate = inject(AppUpdateService);
  private readonly jivuCommandPoller = inject(JivuCommandPollerService);
  private readonly bgRender = inject(BackgroundRenderClientService);
  private readonly webavCanvas = inject(WebavCanvasService);
  protected readonly title = signal('ai-content-generation-fe');

  constructor() {
    // DEV-ONLY: expose the background render client so the worker pipeline can be
    // exercised end-to-end from a headed browser/Playwright. Never enabled in prod.
    if (!environment.production && typeof window !== 'undefined') {
      const w = window as unknown as {
        __bgRender?: BackgroundRenderClientService;
        __bgRenderTest?: () => Promise<unknown>;
        __bgRenderFixTest?: () => Promise<unknown>;
        __bgRenderKeyframeParityTest?: () => Promise<unknown>;
        __bgRenderTransitionParityTest?: () => Promise<unknown>;
        __bgRenderCaptionStickerParityTest?: () => Promise<unknown>;
        __bgRenderShortSourceLoopTest?: () => Promise<unknown>;
        __bgRenderCaptionSizeParityTest?: (w: number, h: number, fontSize?: number) => Promise<unknown>;
        __bgRenderMotionParityTest?: () => Promise<unknown>;
      };
      w.__bgRender = this.bgRender;
      // DEV-ONLY: golden-frame CAPTION SIZE + STYLE parity at an ARBITRARY output (w,h).
      // Renders the same styled caption via the WORKER (real adapter) and the PREVIEW
      // compositor and reports each stack's normalized ink bbox + centre luminance, so a
      // headed run can assert preview == export at 1080×1920 / 720×1280 / 1280×720.
      w.__bgRenderCaptionSizeParityTest = (cw: number, ch: number, fontSize?: number) =>
        import('./core/render-runtime/worker/dev-render-harness').then(m =>
          m.runCaptionSizeStyleParityTest(this.bgRender, this.webavCanvas, cw, ch, fontSize),
        );
      w.__bgRenderTest = () =>
        import('./core/render-runtime/worker/dev-render-harness').then(m =>
          m.runDevRenderTest(this.bgRender),
        );
      // DEV-ONLY: exercise the render-correctness fixes (duplicate same-source clip,
      // primitive sticker + transform, thumbnailCount>1, Urdu font) end-to-end.
      w.__bgRenderFixTest = () =>
        import('./core/render-runtime/worker/dev-render-harness').then(m =>
          m.runDevRenderFixTest(this.bgRender),
        );
      // DEV-ONLY: golden-frame KEYFRAME parity — render the same keyframed clip via
      // the WORKER and the PREVIEW compositor and assert the pixels match (the one
      // shared resolver is applied identically → preview == export).
      w.__bgRenderKeyframeParityTest = () =>
        import('./core/render-runtime/worker/dev-render-harness').then(m =>
          m.runKeyframeParityTest(this.bgRender, this.webavCanvas),
        );
      // DEV-ONLY: golden-frame TRANSITION-BLEND parity — render RED →crossfade→ GREEN via
      // the WORKER and the PREVIEW compositor and assert the midpoint is a real RED+GREEN
      // blend in BOTH and matches (the one shared neighbor+blend module → preview == export).
      w.__bgRenderTransitionParityTest = () =>
        import('./core/render-runtime/worker/dev-render-harness').then(m =>
          m.runTransitionBlendParityTest(this.bgRender, this.webavCanvas),
        );
      // DEV-ONLY: golden-frame CAPTION + STICKER ANIMATION parity (Fix 2) — render an
      // animated caption and an animated sticker via the WORKER (through the real adapter)
      // and the PREVIEW compositor; assert each ANIMATES (fades in) and that preview ==
      // export at sampled times (the Animate tool now exports for text/caption/sticker).
      w.__bgRenderCaptionStickerParityTest = () =>
        import('./core/render-runtime/worker/dev-render-harness').then(m =>
          m.runCaptionStickerParityTest(this.bgRender, this.webavCanvas),
        );
      // DEV-ONLY: SHORT-SOURCE LOOP — render a 2s source on a 5s clip span (the Smart
      // Video window sourceStart:0/sourceEnd:5) via the WORKER and assert the back half
      // (>2s) LOOPS (replays, stays non-blank + changing) instead of freezing/blanking,
      // plus a matched-window clip does NOT loop (the render.worker clipSourceTime fix).
      w.__bgRenderShortSourceLoopTest = () =>
        import('./core/render-runtime/worker/dev-render-harness').then(m =>
          m.runShortSourceLoopTest(this.bgRender),
        );
      // DEV-ONLY: golden-frame MOTION-CLIP parity — a base GREEN video (0.5 box) + one
      // kind:'motion' clip (zoom scale 1→2) rendered via the WORKER (real adapter) and the
      // PREVIEW compositor; asserts the BASE frame zooms (off-centre band lights up) in BOTH
      // and matches, plus a no-motion project is unchanged (the kind:'motion' render feature).
      w.__bgRenderMotionParityTest = () =>
        import('./core/render-runtime/worker/dev-render-harness').then(m =>
          m.runMotionParityTest(this.bgRender, this.webavCanvas),
        );
    }
    effect(() => {
      if (this.authStore.isAuthenticated()) {
        this.jivuCommandPoller.start();
      } else {
        this.jivuCommandPoller.stop();
      }
    });
    effect(() => {
      if (this.authStore.isAuthenticated()) {
        this.appUpdate.refresh();
      }
    });
    effect(() => {
      if (this.authStore.isAuthenticated() && this.appUpdate.requiresUpdate()) {
        this.appUpdate.startAutomaticUpdate();
      }
    });
  }

  protected isOnboardingRoute(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return path.startsWith('/onboarding');
  }

  ngOnInit() {
    this.authStore.initializeFromStorage();
    this.authStore.rehydrateFromCookie().subscribe();
    this.uiStore.initializeTheme();
  }

  protected isPublicRoute(): boolean {
    return ['/', '/workflow', '/ai-keys', '/pricing', '/faq', '/login', '/register', '/confirm-email', '/forgot-password', '/reset-password'].includes(this.router.url.split(/[?#]/)[0]);
  }

  protected showMandatoryNativeUpdate(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return this.authStore.isAuthenticated()
      && path !== '/jivu-overlay'
      && this.appUpdate.requiresUpdate();
  }
}
